import { Component, type ErrorInfo, type ReactNode } from 'react'
import * as Sentry from '@sentry/react'
import { isChunkLoadError, markStaleBundle } from '@/lib/staleBundle'

interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
}

/**
 * Last-resort boundary around the route tree.
 *
 * The one class component in the codebase, because React has no hook
 * equivalent — an error boundary must implement componentDidCatch.
 *
 * Without this, any render throw or failed lazy chunk unmounts the whole tree
 * and leaves a white screen with no way back. That is survivable at a desk and
 * not survivable in a truck, so the fallback always offers a route out.
 */
export class RouteErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Report explicitly. An error a boundary catches is *handled* as far as the
    // runtime is concerned: it never reaches window.onerror, so Sentry's global
    // handler never sees it. Until this call existed, every render crash in the
    // product was invisible — the user got "This screen didn't load" and we got
    // nothing. That is how `client.latitude.toFixed is not a function` survived
    // on every Stars-imported client from ~May to 2026-08-12: the boundary was
    // catching it the whole time and telling no one.
    //
    // No-op unless VITE_SENTRY_DSN was present at build time (see main.tsx):
    // with no client bound, captureException does nothing.
    Sentry.captureException(error, {
      contexts: { react: { componentStack: info.componentStack } },
    })

    // Kept alongside: the console line is what a developer — or an owner with
    // DevTools open — reads when the DSN is unset, and it is the only trace in
    // local dev, where a blank screen is otherwise silent.
    console.error('Route error boundary caught:', error, info.componentStack)

    // A chunk that will not load means this tab's bundle is from an older
    // deploy. Reloading fixes it, so say that instead of "didn't load".
    if (isChunkLoadError(error)) markStaleBundle()
  }

  render() {
    if (!this.state.error) return this.props.children

    // The prompt itself is StaleBundlePrompt, mounted outside this boundary,
    // so a chunk failure seen by both this and vite:preloadError shows once.
    if (isChunkLoadError(this.state.error)) {
      return <div className="min-h-screen bg-gray-50" data-testid="stale-bundle-backdrop" />
    }

    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-gray-50 p-6 text-center">
        <div>
          <h1 className="text-lg font-bold text-gray-900">This screen didn&apos;t load</h1>
          <p className="mt-1 max-w-sm text-sm text-gray-500">
            Something went wrong rendering this page. Reloading usually clears it.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white"
          >
            Reload
          </button>
          <button
            type="button"
            onClick={() => window.location.assign('/')}
            className="rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700"
          >
            Go home
          </button>
        </div>
      </div>
    )
  }
}
